import React, { useState, useEffect } from "react";
import { ReportData } from "./types";
import { AnimatePresence, motion } from "framer-motion";
import {
  modalVariants,
  overlayVariants,
} from "../../../../animations/Animations";
import { useDateOfVisit } from "../../../../contexts/report/DateOfVisitContext";
import ProductSelector from "./custom/ProductSelector";
import ContentSelector from "./custom/ContentSelector";
import { FaEdit } from "react-icons/fa";
import accessURL from "../../../../components/utils/access";
import { toast } from "react-toastify";

interface UpdateModalProps {
  reportData: ReportData | null;
  onClose: () => void;
}

interface FormData {
  objective: string | null;
  dateOfVisit: string;
  content: number[];
  products: number[];
  followupPlan: string;
  customer: string | null;
}

interface FormErrors {
  objective?: string;
  dateOfVisit?: string;
  content?: string;
  products?: string;
  followupPlan?: string;
  customer?: string;
}

const UpdateModal: React.FC<UpdateModalProps> = ({ reportData, onClose }) => {
  const { dateOfVisit, setDateOfVisit } = useDateOfVisit() ?? {};
  const [originalContent, setOriginalContent] = useState<number[]>([]);
  const [originalFollowupPlan, setOriginalFollowupPlan] = useState<string>("");
  const [isContentEdited, setIsContentEdited] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    objective: null,
    dateOfVisit: "",
    content: [],
    products: [],
    followupPlan: "",
    customer: null,
  });

  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (reportData) {
      setDateOfVisit?.(reportData.dateOfVisit);
      const contentIds = reportData.contents.map((content) => content.id);
      setOriginalContent(contentIds);
      setOriginalFollowupPlan(reportData.followupPlan || "");
      setFormData((prev) => ({
        ...prev,
        customer: reportData.customer,
        objective: reportData.objective,
        products: reportData.products.map((product) => product.id),
        content: contentIds,
        followupPlan: reportData.followupPlan || "",
        dateOfVisit: reportData.dateOfVisit || "",
      }));
    }
  }, [reportData, setDateOfVisit]);

  // Check for changes in form data
  useEffect(() => {
    if (reportData) {
      const hasContentChanges =
        JSON.stringify(formData.content) !== JSON.stringify(originalContent);
      const hasFollowupChanges = formData.followupPlan !== originalFollowupPlan;
      const hasProductChanges =
        JSON.stringify(formData.products) !==
        JSON.stringify(reportData.products.map((product) => product.id));

      setHasChanges(
        hasContentChanges || hasFollowupChanges || hasProductChanges
      );
    }
  }, [formData, originalContent, originalFollowupPlan, reportData]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === "dateOfVisit") {
      if (!value && reportData?.dateOfVisit) {
        setDateOfVisit?.(reportData.dateOfVisit);
      } else {
        setDateOfVisit?.(value);
      }
    } else if (name === "followupPlan") {
      setFormData((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, followupPlan: undefined }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleContentChange = (contents: number[]) => {
    setIsContentEdited(true);
    setFormData((prev) => ({ ...prev, content: contents }));
    setErrors((prev) => ({ ...prev, content: undefined }));

    if (contents.length === 0) {
      setErrors((prev) => ({
        ...prev,
        content: "Content selection cannot be empty",
      }));
      setFormData((prev) => ({ ...prev, content: originalContent }));
      setIsContentEdited(false);
    }
  };

  const handleCloseModal = () => {
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.content.length === 0) {
      setErrors((prev) => ({
        ...prev,
        content: "Content selection cannot be empty",
      }));
      return;
    }
    if (!formData.followupPlan.trim()) {
      setErrors((prev) => ({
        ...prev,
        followupPlan: "Follow-up plan cannot be empty",
      }));
      return;
    }

    setIsLoading(true);

    const updatedFormData = {
      ...formData,
      dateOfVisit: dateOfVisit || reportData?.dateOfVisit || "",
      customer: reportData?.customer,
      timeOfVisit: reportData?.timeOfVisit,
      id: reportData?.id,
    };

    try {
      const response = await accessURL.post("/reports/update", updatedFormData);
      if (response.status === 201) {
        toast.success("Report updated successfully!");
        setTimeout(() => {
          window.location.reload();
          handleCloseModal();
        }, 2000);
      } else {
        toast.error("Failed to update report. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred while updating the report.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden p-4 sm:p-6 md:p-8"
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50"
          onClick={handleCloseModal}
        />
        <motion.div
          className="bg-white w-full max-w-4xl rounded-lg shadow-lg relative z-10 flex flex-col max-h-[90vh] overflow-hidden"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {/* Header */}
          <div className="flex-shrink-0 sticky top-0 bg-white px-6 py-4 border-b border-gray-200 rounded-t-lg z-20">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-semibold text-gray-800">
                Update Report
              </h2>
              <button
                className="text-gray-500 hover:text-gray-700 transition-colors"
                onClick={handleCloseModal}
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto">
            <form onSubmit={handleSubmit} className="h-full">
              {/* Follow-up Plan with Edit Icon */}
              <div className="relative bg-gray-50 p-6 border-b border-gray-200">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Follow-up Plan
                  </h3>
                  <FaEdit
                    className="text-blue-500 hover:text-blue-600 cursor-pointer"
                    size={20}
                  />
                </div>
                <textarea
                  className={`w-full p-3 border rounded-lg resize-none ${
                    errors.followupPlan ? "border-red-500" : "border-gray-300"
                  }`}
                  rows={4}
                  placeholder="Enter follow-up plan"
                  name="followupPlan"
                  value={formData.followupPlan}
                  onChange={handleInputChange}
                />
                {errors.followupPlan && (
                  <p className="text-red-500 text-sm mt-2">
                    {errors.followupPlan}
                  </p>
                )}
              </div>

              {/* Content Selector with Edit Icon */}
              <div className="relative bg-gray-50 p-6 border-b border-gray-200">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Content Selection
                  </h3>
                  <FaEdit
                    className="text-blue-500 hover:text-blue-600 cursor-pointer"
                    size={20}
                  />
                </div>
                <ContentSelector
                  selectedContents={formData.content}
                  onContentsChange={handleContentChange}
                  error={errors.content}
                />
                {errors.content && (
                  <p className="text-red-500 text-sm mt-2">{errors.content}</p>
                )}
              </div>

              {/* Form Fields */}
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Date of Visit */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date of Visit
                    </label>
                    <input
                      type="date"
                      className="w-full p-3 border border-gray-300 bg-gray-100 rounded-lg cursor-not-allowed focus:outline-none"
                      name="dateOfVisit"
                      value={dateOfVisit || ""}
                      onChange={handleInputChange}
                      readOnly
                    />
                  </div>

                  {/* Customer */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Customer
                    </label>
                    <input
                      type="text"
                      className="w-full p-3 border border-gray-300 bg-gray-100 rounded-lg cursor-not-allowed focus:outline-none"
                      value={reportData?.customer || ""}
                      readOnly
                    />
                  </div>

                  {/* Time of Visit */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Time of Visit
                    </label>
                    <input
                      type="text"
                      className="w-full p-3 border border-gray-300 bg-gray-100 rounded-lg cursor-not-allowed focus:outline-none"
                      value={reportData?.timeOfVisit || ""}
                      readOnly
                    />
                  </div>

                  {/* Objective */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Objective
                    </label>
                    <input
                      type="text"
                      className="w-full p-3 border border-gray-300 bg-gray-100 rounded-lg cursor-not-allowed focus:outline-none"
                      placeholder="Enter objective"
                      name="objective"
                      value={formData.objective || ""}
                      readOnly
                    />
                  </div>
                </div>

                {/* Product Selector */}
                <div className="mt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Products
                  </h3>
                  <ProductSelector
                    selectedProducts={formData.products}
                    onProductsChange={(products) => {
                      setFormData((prev) => ({ ...prev, products }));
                      setErrors((prev) => ({ ...prev, products: undefined }));
                    }}
                    error={errors.products}
                    products={reportData?.products || []}
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-gray-200">
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={!hasChanges || isLoading}
                    className={`px-6 py-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      !hasChanges || isLoading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-500 hover:bg-blue-600"
                    } text-white`}
                  >
                    {isLoading ? (
                      <span className="inline-flex items-center">
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Updating...
                      </span>
                    ) : (
                      "Update"
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UpdateModal;
